import { DropdownMenu } from 'radix-ui';
import { ADJUSTMENTS, ADJUSTMENT_ORDER } from '../adjustments';
import type { AdjustmentId } from '../adjustments';
import { FILTERS, FILTER_GROUPS } from '../filters';
import {
  actualPixels,
  addAdjustmentLayer,
  addFillLayer,
  addGuidesFromSelection,
  addLayer,
  applyAdjustment,
  clearGuides,
  clearLayerEffects,
  closeDocument,
  convertToSmartObject,
  copySelection,
  cropToSelection,
  cutSelection,
  deleteLayer,
  deselect,
  duplicateLayerOrViaCopy,
  fillWithColor,
  fitToScreen,
  flattenImage,
  groupActiveLayer,
  invertSelection,
  layerViaCut,
  mergeDown,
  mergeVisible,
  newSmartObjectFromLayer,
  nudgeActiveLayer,
  pasteClipboard,
  placeImageAsLayer,
  rasterizeSmartObject,
  redo,
  saveDocumentAsPdf,
  saveDocumentAsPsd,
  selectAll,
  setViewRotation,
  stampVisible,
  toggleClippingMask,
  toggleSelectionEdges,
  undo,
  ungroupActiveLayer,
  zoomStep,
} from '../state/actions';
import {
  addLayerMask,
  applyLayerMask,
  channelToSelection,
  invertLayerMask,
  removeLayerMask,
  selectionFromLayerMask,
  selectionToChannel,
  toggleLayerMaskEnabled,
  toggleLayerMaskLinked,
  toggleLayerMaskLocked,
  toggleQuickMask,
} from '../state/maskActions';
import {
  pathFromShapeLayer,
  rasterizeShapeLayer,
  removeVectorMask,
  vectorMaskFromPath,
} from '../state/vectorActions';
import { rasterizeTextLayer } from '../state/textActions';
import { growSelectionByColor, reselect, selectSimilarColors } from '../state/selectionActions';
import { countLayers, findLayerById, isGroup, isSurfaceLayer, locateLayer } from '../core/layerTree';
import { defineBrushFromLayer, importBrushFile } from '../state/brushActions';
import { definePatternFromLayer } from '../state/patternActions';
import { commandChord } from '../state/keymap';
import {
  alignActiveLayer,
  ALIGN_LABELS,
  distributeLayers,
  type AlignEdge,
} from '../state/alignActions';
import { startTransformSelection } from '../tools/transform';
import { autosaveNow } from '../state/session';
import { messagesFor } from '../i18n';
import { getActiveDocument, getHistoryView, useAppStore } from '../state/store';
import { BRUSH_ACCEPT, pickFiles, pickImageFiles } from '../io/import';
import { openFilesFromPicker } from '../state/openFiles';
import { defaultAdjustmentState } from '../adjustments';
import {
  TRANSFORM_MODES,
  QUICK_TRANSFORMS,
  applyQuickTransform,
  beginTransform,
  startFreeTransform,
} from './transformMenu';

interface Entry {
  label: string;
  shortcut?: string;
  disabled?: boolean;
  onSelect?: () => void;
  separator?: never;
}

type MenuEntry = Entry | { separator: true };

const ADJUSTMENT_COMMANDS: Partial<Record<AdjustmentId, string>> = {
  levels: 'image.levels',
  hsl: 'image.hsl',
};

const ALIGN_EDGES: AlignEdge[] = ['left', 'centerX', 'right', 'top', 'centerY', 'bottom'];

/** Menu items borrow their chord from the keymap so the two cannot drift apart. */
const chord = (commandId: string): string | undefined => commandChord(commandId);

function MenuGroup({ label, entries }: { label: string; entries: MenuEntry[] }) {
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger className="menubar__trigger">{label}</DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content className="menu-content" sideOffset={2} align="start">
          {entries.map((entry, index) =>
            'separator' in entry ? (
              <DropdownMenu.Separator key={index} className="menu-separator" />
            ) : (
              <DropdownMenu.Item
                key={entry.label}
                className="menu-item"
                disabled={entry.disabled}
                onSelect={entry.onSelect}
              >
                <span>{entry.label}</span>
                {entry.shortcut && <span className="menu-item__shortcut">{entry.shortcut}</span>}
              </DropdownMenu.Item>
            ),
          )}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

export function MenuBar() {
  const store = useAppStore();
  const doc = getActiveDocument(store);
  const view = doc ? (store.views[doc.id] ?? null) : null;
  const history = getHistoryView(store, store.activeDocumentId);
  const activeLayer = doc ? findLayerById(doc.layers, doc.activeLayerId) : null;
  const activeLocation =
    doc && doc.activeLayerId ? locateLayer(doc.layers, doc.activeLayerId) : null;
  const noDoc = !doc;
  const noActiveLayer = !activeLayer;

  const ADJUSTMENT_LAYER_IDS = [
    'brightnessContrast',
    'hsl',
    'levels',
    'invert',
    'desaturate',
  ] as const satisfies readonly AdjustmentId[];

  const placeFile = async () => {
    const files = await pickImageFiles();
    if (files[0]) await placeImageAsLayer(files[0]);
  };

  const pickBrushes = async () => {
    const files = await pickFiles(BRUSH_ACCEPT, true);
    for (const file of files) await importBrushFile(file);
  };

  return (
    <div className="menubar">
      <span className="menubar__brand">PIXOTAH</span>

      <MenuGroup
        label="File"
        entries={[
          {
            label: 'New…',
            shortcut: chord('file.new'),
            onSelect: () => store.openDialog({ kind: 'newDocument' }),
          },
          {
            label: 'Open…',
            shortcut: chord('file.open'),
            onSelect: () => void openFilesFromPicker(),
          },
          { label: 'Place…', disabled: noDoc, onSelect: () => void placeFile() },
          { label: 'Open Brushes…', onSelect: () => void pickBrushes() },
          { separator: true },
          {
            label: 'Save as PSD…',
            shortcut: chord('file.save'),
            disabled: noDoc,
            onSelect: () => void saveDocumentAsPsd(),
          },
          {
            label: 'Export As…',
            shortcut: chord('file.export'),
            disabled: noDoc,
            onSelect: () => store.openDialog({ kind: 'export' }),
          },
          {
            label: 'Save as PDF…',
            disabled: noDoc,
            onSelect: () => void saveDocumentAsPdf(),
          },
          { separator: true },
          {
            label: 'Save Session Now',
            disabled: noDoc,
            onSelect: () => void autosaveNow(),
          },
          {
            label: 'Close',
            shortcut: chord('file.close'),
            disabled: noDoc,
            onSelect: () => doc && closeDocument(doc.id),
          },
        ]}
      />

      <MenuGroup
        label="Edit"
        entries={[
          {
            label: 'Undo',
            shortcut: chord('edit.undo'),
            disabled: !history.canUndo,
            onSelect: undo,
          },
          {
            label: 'Redo',
            shortcut: chord('edit.redo'),
            disabled: !history.canRedo,
            onSelect: redo,
          },
          { separator: true },
          {
            label: 'Cut',
            shortcut: chord('edit.cut'),
            disabled: noDoc,
            onSelect: cutSelection,
          },
          {
            label: 'Copy',
            shortcut: chord('edit.copy'),
            disabled: noDoc,
            onSelect: () => copySelection(false),
          },
          {
            label: 'Copy Merged',
            shortcut: chord('edit.copyMerged'),
            disabled: noDoc,
            onSelect: () => copySelection(true),
          },
          {
            label: 'Paste',
            shortcut: chord('edit.paste'),
            disabled: noDoc,
            onSelect: () => void pasteClipboard(false),
          },
          {
            label: 'Paste in Place',
            shortcut: chord('edit.pasteInPlace'),
            disabled: noDoc,
            onSelect: () => void pasteClipboard(true),
          },
          { separator: true },
          {
            label: 'Free Transform',
            shortcut: chord('edit.transform'),
            disabled: noDoc,
            onSelect: startFreeTransform,
          },
          ...TRANSFORM_MODES.map(({ mode, label }) => ({
            label: `Transform: ${label}`,
            disabled: noDoc,
            onSelect: () => beginTransform(mode),
          })),
          ...QUICK_TRANSFORMS.map(({ kind, label }) => ({
            label: `Transform: ${label}`,
            disabled: noDoc,
            onSelect: () => applyQuickTransform(kind),
          })),
          {
            label: 'Fill…',
            shortcut: chord('edit.fill'),
            disabled: noDoc,
            onSelect: () => store.openDialog({ kind: 'fill' }),
          },
          {
            label: 'Stroke…',
            disabled: !doc?.hasSelection,
            onSelect: () => store.openDialog({ kind: 'stroke' }),
          },
          {
            label: 'Fill with Foreground Color',
            shortcut: chord('edit.fillForeground'),
            disabled: noDoc,
            onSelect: () => fillWithColor(store.foreground),
          },
          {
            label: 'Fill with Background Color',
            shortcut: chord('edit.fillBackground'),
            disabled: noDoc,
            onSelect: () => fillWithColor(store.background),
          },
          { separator: true },
          {
            label: 'Define New Brush',
            disabled: noActiveLayer,
            onSelect: defineBrushFromLayer,
          },
          {
            label: 'Define New Pattern',
            disabled: noActiveLayer,
            onSelect: definePatternFromLayer,
          },
          { separator: true },
          {
            label: 'Keyboard Shortcuts',
            shortcut: chord('edit.shortcuts'),
            onSelect: () => store.openDialog({ kind: 'shortcuts' }),
          },
        ]}
      />

      <MenuGroup
        label="Image"
        entries={[
          {
            label: 'Image Size…',
            shortcut: chord('image.size'),
            disabled: noDoc,
            onSelect: () => store.openDialog({ kind: 'imageSize' }),
          },
          {
            label: 'Canvas Size…',
            shortcut: chord('image.canvasSize'),
            disabled: noDoc,
            onSelect: () => store.openDialog({ kind: 'canvasSize' }),
          },
          {
            label: 'Crop',
            shortcut: chord('image.crop'),
            disabled: !doc?.hasSelection,
            onSelect: cropToSelection,
          },
          { separator: true },
          ...ADJUSTMENT_ORDER.map((id) => ({
            label: `${ADJUSTMENTS[id].label}…`,
            shortcut: ADJUSTMENT_COMMANDS[id] ? chord(ADJUSTMENT_COMMANDS[id]) : undefined,
            disabled: noDoc,
            onSelect: () => store.openDialog({ kind: 'adjustment', adjustment: id }),
          })),
          { separator: true },
          {
            label: 'Invert',
            shortcut: chord('image.invert'),
            disabled: noDoc,
            onSelect: () => applyAdjustment(defaultAdjustmentState('invert')),
          },
          {
            label: 'Desaturate',
            shortcut: chord('image.desaturate'),
            disabled: noDoc,
            onSelect: () => applyAdjustment(defaultAdjustmentState('desaturate')),
          },
          { separator: true },
          {
            label: 'Vectorize Bitmap…',
            disabled: noActiveLayer,
            onSelect: () => store.openDialog({ kind: 'vectorize' }),
          },
        ]}
      />

      <MenuGroup
        label="Layer"
        entries={[
          { label: 'New Layer', shortcut: chord('layer.new'), disabled: noDoc, onSelect: addLayer },
          {
            label: 'Duplicate Layer',
            shortcut: chord('layer.duplicate'),
            disabled: noActiveLayer,
            onSelect: duplicateLayerOrViaCopy,
          },
          {
            label: 'Layer via Cut',
            shortcut: chord('layer.viaCut'),
            disabled: noActiveLayer,
            onSelect: layerViaCut,
          },
          {
            label: 'Group Layers',
            shortcut: chord('layer.group'),
            disabled: noActiveLayer,
            onSelect: groupActiveLayer,
          },
          {
            label: 'Ungroup Layers',
            shortcut: chord('layer.ungroup'),
            disabled: noActiveLayer || !activeLayer || !isGroup(activeLayer),
            onSelect: ungroupActiveLayer,
          },
          {
            label: 'Create/Release Clipping Mask',
            shortcut: chord('layer.clippingMask'),
            disabled: noActiveLayer,
            onSelect: () => activeLayer && toggleClippingMask(activeLayer.id),
          },
          ...ADJUSTMENT_LAYER_IDS.map((id) => ({
            label: `Adjustment Layer: ${ADJUSTMENTS[id].label}`,
            disabled: noDoc,
            onSelect: () => addAdjustmentLayer(id),
          })),
          {
            label: 'Fill Layer: Solid Color',
            disabled: noDoc,
            onSelect: () => addFillLayer('solid'),
          },
          {
            label: 'Fill Layer: Gradient',
            disabled: noDoc,
            onSelect: () => addFillLayer('gradient'),
          },
          {
            label: 'Fill Layer: Pattern',
            disabled: noDoc,
            onSelect: () => addFillLayer('pattern'),
          },
          { separator: true },
          {
            label: 'Layer Style…',
            disabled: noActiveLayer || !activeLayer,
            onSelect: () => store.openDialog({ kind: 'layerStyle' }),
          },
          {
            label: 'Clear Layer Style',
            disabled: noActiveLayer || !activeLayer || !isSurfaceLayer(activeLayer),
            onSelect: () => activeLayer && clearLayerEffects(activeLayer.id),
          },
          {
            label: 'Convert to Smart Object',
            disabled: noActiveLayer || !activeLayer || !isSurfaceLayer(activeLayer) || activeLayer.kind === 'smart',
            onSelect: () => convertToSmartObject(),
          },
          {
            label: 'Rasterize Smart Object',
            disabled: !activeLayer || activeLayer.kind !== 'smart',
            onSelect: () => rasterizeSmartObject(),
          },
          {
            label: 'New Smart Object via Copy',
            disabled: noActiveLayer || !activeLayer || !isSurfaceLayer(activeLayer),
            onSelect: () => newSmartObjectFromLayer(),
          },
          { separator: true },
          {
            label: 'Delete Layer',
            disabled: noActiveLayer || (doc ? countLayers(doc.layers) <= 1 : true),
            onSelect: () => activeLayer && deleteLayer(activeLayer.id),
          },
          { separator: true },
          ...ALIGN_EDGES.map((edge) => ({
            label: `Align ${ALIGN_LABELS[edge]}`,
            disabled: noActiveLayer,
            onSelect: () => alignActiveLayer(edge),
          })),
          {
            label: 'Distribute Horizontally',
            disabled: noActiveLayer,
            onSelect: () => distributeLayers('horizontal'),
          },
          {
            label: 'Distribute Vertically',
            disabled: noActiveLayer,
            onSelect: () => distributeLayers('vertical'),
          },
          { separator: true },
          {
            label: 'Raster Mask: Add — Reveal All',
            disabled: noActiveLayer || Boolean(activeLayer?.hasMask),
            onSelect: () => activeLayer && addLayerMask(activeLayer.id),
          },
          {
            label: 'Raster Mask: Add — Hide Selection',
            disabled: noActiveLayer || Boolean(activeLayer?.hasMask) || !doc?.hasSelection,
            onSelect: () => activeLayer && addLayerMask(activeLayer.id, true),
          },
          {
            label: 'Raster Mask: Delete',
            disabled: !activeLayer?.hasMask,
            onSelect: () => activeLayer && removeLayerMask(activeLayer.id),
          },
          {
            label: 'Raster Mask: Apply',
            disabled: !activeLayer?.hasMask || isGroup(activeLayer),
            onSelect: () => activeLayer && applyLayerMask(activeLayer.id),
          },
          {
            label: 'Raster Mask: Invert',
            disabled: !activeLayer?.hasMask,
            onSelect: () => activeLayer && invertLayerMask(activeLayer.id),
          },
          {
            label: activeLayer?.maskEnabled ? 'Raster Mask: Disable' : 'Raster Mask: Enable',
            disabled: !activeLayer?.hasMask,
            onSelect: () => activeLayer && toggleLayerMaskEnabled(activeLayer.id),
          },
          {
            label: activeLayer?.maskLinked ? 'Raster Mask: Unlink' : 'Raster Mask: Link',
            disabled: !activeLayer?.hasMask,
            onSelect: () => activeLayer && toggleLayerMaskLinked(activeLayer.id),
          },
          {
            label: activeLayer?.lockMask ? 'Raster Mask: Unlock' : 'Raster Mask: Lock',
            disabled: !activeLayer?.hasMask,
            onSelect: () => activeLayer && toggleLayerMaskLocked(activeLayer.id),
          },
          { separator: true },
          {
            label: 'Vector Mask: From Path',
            disabled: noActiveLayer || !doc?.activePathId,
            onSelect: () => activeLayer && vectorMaskFromPath(activeLayer.id),
          },
          {
            label: 'Vector Mask: Delete',
            disabled: !activeLayer?.vectorMask,
            onSelect: () => activeLayer && removeVectorMask(activeLayer.id),
          },
          { separator: true },
          {
            label: 'Path from Shape',
            disabled: activeLayer?.kind !== 'shape',
            onSelect: () => activeLayer && pathFromShapeLayer(activeLayer.id),
          },
          {
            label: 'Rasterize Shape',
            disabled: activeLayer?.kind !== 'shape',
            onSelect: () => activeLayer && rasterizeShapeLayer(activeLayer.id),
          },
          {
            label: 'Rasterize Type',
            disabled: activeLayer?.kind !== 'text',
            onSelect: () => activeLayer && rasterizeTextLayer(activeLayer.id),
          },
          { separator: true },
          {
            label: 'Bring Forward',
            shortcut: chord('layer.bringForward'),
            disabled: noActiveLayer,
            onSelect: () => nudgeActiveLayer('forward'),
          },
          {
            label: 'Send Backward',
            shortcut: chord('layer.sendBackward'),
            disabled: noActiveLayer,
            onSelect: () => nudgeActiveLayer('backward'),
          },
          {
            label: 'Bring to Front',
            shortcut: chord('layer.bringToFront'),
            disabled: noActiveLayer,
            onSelect: () => nudgeActiveLayer('front'),
          },
          {
            label: 'Send to Back',
            shortcut: chord('layer.sendToBack'),
            disabled: noActiveLayer,
            onSelect: () => nudgeActiveLayer('back'),
          },
          { separator: true },
          {
            label: 'Merge Down',
            shortcut: chord('layer.mergeDown'),
            disabled: !activeLocation || activeLocation.index <= 0,
            onSelect: () => activeLayer && mergeDown(activeLayer.id),
          },
          {
            label: 'Merge Visible',
            shortcut: chord('layer.mergeVisible'),
            disabled: noDoc,
            onSelect: mergeVisible,
          },
          {
            label: 'Stamp Visible',
            shortcut: chord('layer.stampVisible'),
            disabled: noDoc,
            onSelect: stampVisible,
          },
          {
            label: 'Flatten Image',
            shortcut: chord('layer.flatten'),
            disabled: noDoc || countLayers(doc?.layers ?? []) <= 1,
            onSelect: flattenImage,
          },
        ]}
      />

      <MenuGroup
        label="Filter"
        entries={[
          ...FILTER_GROUPS.flatMap(({ group, ids }, groupIndex) => [
            ...(groupIndex > 0 ? [{ separator: true as const }] : []),
            ...ids.map((id) => ({
              label: `${group}: ${FILTERS[id].label}…`,
              disabled: noActiveLayer,
              onSelect: () => store.openDialog({ kind: 'filter', filter: id }),
            })),
          ]),
          { separator: true },
          {
            label: 'Liquify…',
            disabled: noActiveLayer,
            onSelect: () => store.openDialog({ kind: 'liquify' }),
          },
          {
            label: 'Other: Offset…',
            disabled: noActiveLayer,
            onSelect: () => store.openDialog({ kind: 'offset' }),
          },
        ]}
      />

      <MenuGroup
        label="Select"
        entries={[
          { label: 'All', shortcut: chord('select.all'), disabled: noDoc, onSelect: selectAll },
          {
            label: 'Deselect',
            shortcut: chord('select.none'),
            disabled: !doc?.hasSelection,
            onSelect: deselect,
          },
          {
            label: 'Reselect',
            shortcut: chord('select.reselect'),
            disabled: noDoc,
            onSelect: reselect,
          },
          {
            label: 'Inverse',
            shortcut: chord('select.inverse'),
            disabled: noDoc,
            onSelect: invertSelection,
          },
          { separator: true },
          {
            label: 'Color Range…',
            disabled: noDoc,
            onSelect: () => store.openDialog({ kind: 'colorRange' }),
          },
          { separator: true },
          {
            label: 'Modify: Border…',
            disabled: !doc?.hasSelection,
            onSelect: () => store.openDialog({ kind: 'selectionModify', modify: 'border' }),
          },
          {
            label: 'Modify: Smooth…',
            disabled: !doc?.hasSelection,
            onSelect: () => store.openDialog({ kind: 'selectionModify', modify: 'smooth' }),
          },
          {
            label: 'Modify: Expand…',
            disabled: !doc?.hasSelection,
            onSelect: () => store.openDialog({ kind: 'selectionModify', modify: 'expand' }),
          },
          {
            label: 'Modify: Contract…',
            disabled: !doc?.hasSelection,
            onSelect: () => store.openDialog({ kind: 'selectionModify', modify: 'contract' }),
          },
          {
            label: 'Modify: Feather…',
            shortcut: chord('select.feather'),
            disabled: !doc?.hasSelection,
            onSelect: () => store.openDialog({ kind: 'selectionModify', modify: 'feather' }),
          },
          { separator: true },
          { label: 'Grow', disabled: !doc?.hasSelection, onSelect: growSelectionByColor },
          { label: 'Similar', disabled: !doc?.hasSelection, onSelect: selectSimilarColors },
          { separator: true },
          {
            label: 'Refine Edge…',
            disabled: !doc?.hasSelection,
            onSelect: () => store.openDialog({ kind: 'refineEdge' }),
          },
          {
            label: 'Transform Selection',
            disabled: !doc?.hasSelection,
            onSelect: startTransformSelection,
          },
          { separator: true },
          {
            label: 'Save Selection',
            disabled: !doc?.hasSelection,
            onSelect: selectionToChannel,
          },
          ...(doc?.channels ?? []).map((channel) => ({
            label: `Load Selection: ${channel.name}`,
            onSelect: () => channelToSelection(channel.id),
          })),
          {
            label: 'Load Selection: Layer Mask',
            disabled: !activeLayer?.hasMask,
            onSelect: () => activeLayer && selectionFromLayerMask(activeLayer.id),
          },
          { separator: true },
          {
            label: `${store.quickMask ? 'Exit' : 'Enter'} Quick Mask`,
            shortcut: chord('tools.quickMask'),
            disabled: noDoc,
            onSelect: toggleQuickMask,
          },
        ]}
      />

      <MenuGroup
        label="View"
        entries={[
          {
            label: 'Zoom In',
            shortcut: chord('view.zoomIn'),
            disabled: noDoc,
            onSelect: () => zoomStep(1),
          },
          {
            label: 'Zoom Out',
            shortcut: chord('view.zoomOut'),
            disabled: noDoc,
            onSelect: () => zoomStep(-1),
          },
          {
            label: 'Fit on Screen',
            shortcut: chord('view.fit'),
            disabled: noDoc,
            onSelect: fitToScreen,
          },
          {
            label: 'Actual Pixels',
            shortcut: chord('view.actualPixels'),
            disabled: noDoc,
            onSelect: actualPixels,
          },
          {
            label: 'Extras',
            shortcut: chord('view.extras'),
            disabled: noDoc,
            onSelect: toggleSelectionEdges,
          },
          {
            label: 'Rotate View…',
            disabled: noDoc,
            onSelect: () => store.openDialog({ kind: 'rotateView' }),
          },
          {
            label: 'Reset View Rotation',
            disabled: noDoc || (view?.rotation ?? 0) === 0,
            onSelect: () => setViewRotation(0),
          },
          { separator: true },
          {
            label: `${store.showRulers ? 'Hide' : 'Show'} Rulers`,
            shortcut: chord('view.rulers'),
            onSelect: store.toggleRulers,
          },
          {
            label: `${store.showGuides ? 'Hide' : 'Show'} Guides`,
            shortcut: chord('view.guides'),
            onSelect: store.toggleGuides,
          },
          {
            label: `${store.showGrid ? 'Hide' : 'Show'} Grid`,
            shortcut: chord('view.grid'),
            onSelect: store.toggleGrid,
          },
          {
            label: `${store.guidesLocked ? 'Unlock' : 'Lock'} Guides`,
            onSelect: store.toggleGuidesLocked,
          },
          {
            label: 'New Guide…',
            disabled: noDoc,
            onSelect: () => store.openDialog({ kind: 'newGuide' }),
          },
          {
            label: 'Guides from Selection',
            disabled: !doc?.hasSelection,
            onSelect: addGuidesFromSelection,
          },
          {
            label: 'Clear Guides',
            disabled: !doc || doc.guides.length === 0,
            onSelect: clearGuides,
          },
          { separator: true },
          {
            label: `Snap${store.snap.enabled ? ' (on)' : ''}`,
            shortcut: chord('view.snap'),
            onSelect: () => store.patchSnap({ enabled: !store.snap.enabled }),
          },
          {
            label: `Snap To Guides${store.snap.toGuides ? ' (on)' : ''}`,
            onSelect: () => store.patchSnap({ toGuides: !store.snap.toGuides }),
          },
          {
            label: `Snap To Grid${store.snap.toGrid ? ' (on)' : ''}`,
            onSelect: () => store.patchSnap({ toGrid: !store.snap.toGrid }),
          },
          {
            label: `Snap To Document Bounds${store.snap.toDocument ? ' (on)' : ''}`,
            onSelect: () => store.patchSnap({ toDocument: !store.snap.toDocument }),
          },
          {
            label: 'Grid Settings…',
            onSelect: () => store.openDialog({ kind: 'gridSettings' }),
          },
          { separator: true },
          {
            label: `${store.chromeHidden ? 'Show' : 'Hide'} All Panels`,
            shortcut: chord('view.chrome'),
            onSelect: store.toggleChrome,
          },
          {
            label: `${store.panels.navigator ? 'Hide' : 'Show'} Navigator`,
            onSelect: () => store.togglePanel('navigator'),
          },
          {
            label: `${store.panels.layers ? 'Hide' : 'Show'} Layers Panel`,
            shortcut: chord('view.layersPanel'),
            onSelect: () => store.togglePanel('layers'),
          },
          {
            label: `${store.panels.brush ? 'Hide' : 'Show'} Brush Panel`,
            shortcut: chord('view.brushPanel'),
            onSelect: () => store.togglePanel('brush'),
          },
          {
            label: `${store.panels.channels ? 'Hide' : 'Show'} Channels Panel`,
            onSelect: () => store.togglePanel('channels'),
          },
          {
            label: `${store.panels.paths ? 'Hide' : 'Show'} Paths Panel`,
            shortcut: chord('view.pathsPanel'),
            onSelect: () => store.togglePanel('paths'),
          },
          {
            label: `${store.panels.properties ? 'Hide' : 'Show'} Properties Panel`,
            onSelect: () => store.togglePanel('properties'),
          },
          {
            label: `${store.panels.info ? 'Hide' : 'Show'} Info Panel`,
            shortcut: chord('view.infoPanel'),
            onSelect: () => store.togglePanel('info'),
          },
          {
            label: `${store.panels.histogram ? 'Hide' : 'Show'} Histogram Panel`,
            onSelect: () => store.togglePanel('histogram'),
          },
          {
            label: `${store.panels.color ? 'Hide' : 'Show'} Color Panel`,
            shortcut: chord('view.colorPanel'),
            onSelect: () => store.togglePanel('color'),
          },
          {
            label: `${store.panels.history ? 'Hide' : 'Show'} History`,
            shortcut: chord('view.historyPanel'),
            onSelect: () => store.togglePanel('history'),
          },
        ]}
      />

      <MenuGroup
        label={messagesFor(store.locale).helpMenu}
        entries={[
          {
            label: messagesFor(store.locale).tutorial,
            onSelect: () => store.openDialog({ kind: 'tutorial' }),
          },
          {
            label: messagesFor(store.locale).language,
            onSelect: () => store.openDialog({ kind: 'language' }),
          },
          { separator: true },
          {
            label: messagesFor(store.locale).keyboardShortcuts,
            shortcut: chord('edit.shortcuts'),
            onSelect: () => store.openDialog({ kind: 'shortcuts' }),
          },
        ]}
      />
    </div>
  );
}
